import { CollectionConfig } from "@rebasepro/types";
import jobsCollection from "./jobs";

/**
 * What we told a company about a vacancy it sent us.
 *
 * The log behind `backend/src/services/job-notices.ts`: the receipt when a
 * submission lands, the email when it is published, and the note when it is
 * sent back. One row per attempt, and the row is written only after the mail
 * server answered — so `Enviado` means the server accepted it, `No salió` says
 * what went wrong, and `No se envía` says why we chose not to write. Those three
 * must never look alike: a vacancy returned with a note that never left is a
 * company waiting on an answer it will not get.
 *
 * Read on the vacancy's own cover (`views/JobOverview.tsx`, via `SubmissionReview`, "Lo que le hemos
 * contado"); this collection is how that view queries it. Out of the drawer:
 * nobody works here, they look. Table, constraints and RLS in drizzle/0066.
 */
const jobNoticesCollection: CollectionConfig = {
    name: "Correos a empresas",
    singularName: "Correo a la empresa",
    slug: "job-notices",
    table: "job_notices",
    securityRules: [
        {
            // Admin only. The company reads the same facts in its inbox and on
            // its dashboard; this is the team's record of having told it.
            name: "admin_full_access",
            operation: "all",
            roles: ["admin"]
        }
    ],
    properties: {
        id: {
            name: "ID",
            type: "string",
            isId: "uuid"
        },
        job_id: {
            name: "Vacante",
            type: "relation",
            relation: {
                kind: "belongsTo",
                target: () => jobsCollection,
                localKey: "job_id"
            },
            validation: { required: true },
            admin: { readOnly: true }
        },
        kind: {
            name: "Qué le contamos",
            type: "string",
            enum: [
                { id: "received", label: "Que la recibimos", color: "blue" },
                { id: "published", label: "Que está publicada", color: "green" },
                { id: "returned", label: "Que necesita cambios", color: "purple" }
            ],
            admin: { readOnly: true }
        },
        status: {
            name: "Resultado",
            type: "string",
            enum: [
                { id: "sent", label: "Enviado", color: "green" },
                { id: "failed", label: "No salió", color: "red" },
                { id: "skipped", label: "No se envía", color: "gray" }
            ],
            description: "«Enviado» solo cuando el servidor de correo lo aceptó. «No se envía» es una decisión, y «Detalle» dice cuál.",
            admin: { readOnly: true }
        },
        recipient: {
            name: "Para",
            type: "string",
            admin: { readOnly: true }
        },
        subject: {
            name: "Asunto",
            type: "string",
            admin: { readOnly: true }
        },
        note: {
            name: "Nota enviada",
            type: "string",
            description: "Al devolverla: la nota tal y como salió. La de la vacante se borra al publicarla; esta queda.",
            admin: { readOnly: true, multiline: true }
        },
        public_url: {
            name: "Enlace a la vacante",
            type: "string",
            url: true,
            description: "Solo si la página respondía cuando se escribió el correo. Vacío: el correo enlazó su panel, porque el sitio aún no se había reconstruido.",
            admin: { readOnly: true }
        },
        detail: {
            name: "Detalle",
            type: "string",
            description: "Qué respondió el servidor cuando no salió, o por qué no se escribe.",
            admin: { readOnly: true, multiline: true }
        },
        message_id: {
            name: "Message-ID",
            type: "string",
            admin: { readOnly: true }
        },
        created_at: {
            name: "Cuándo",
            type: "date",
            autoValue: "on_create",
            admin: { readOnly: true }
        }
    },
    admin: {
        group: "Vacantes",
        icon: "Mail",
        hideFromNavigation: true,
        sort: ["created_at", "desc"],
        listProperties: ["job_id", "kind", "status", "recipient", "created_at"],
        display: {
            title: "subject",
            subtitle: "recipient",
            status: "status",
            date: "created_at"
        },
        previewProperties: ["kind", "status", "recipient", "created_at"]
    }
};

export default jobNoticesCollection;
